import { getChunkLoadErrorUrl } from './isChunkLoadError';

/**
 * Why a lazy chunk failed to load. Values are used as a metric tag, so keep
 * the set small and stable.
 */
export const ChunkLoadFailureReason = {
  /** The asset is gone from the host: the app was redeployed under the user. */
  STALE_DEPLOY: 'stale_deploy',
  /** The client is offline or the request never completed. */
  NETWORK: 'network',
  /** The host or CDN answered with a 5xx, 408 or 429. */
  SERVER: 'server',
  /** The asset is available now, so the first attempt failed transiently. */
  TRANSIENT: 'transient',
  /** The cause could not be determined. */
  UNKNOWN: 'unknown',
} as const;

export type ChunkLoadFailureReason =
  (typeof ChunkLoadFailureReason)[keyof typeof ChunkLoadFailureReason];

/** How long to wait for the probe before treating the request as failed. */
const PROBE_TIMEOUT_MS = 5000;

const classifyStatus = (response: Response): ChunkLoadFailureReason => {
  const { status } = response;

  if (status === 404 || status === 410) {
    return ChunkLoadFailureReason.STALE_DEPLOY;
  }
  if (status === 408 || status === 429 || status >= 500) {
    return ChunkLoadFailureReason.SERVER;
  }
  if (response.ok) {
    // A host with an SPA fallback answers a missing asset with index.html.
    const contentType = response.headers.get('content-type') ?? '';
    return contentType.includes('text/html')
      ? ChunkLoadFailureReason.STALE_DEPLOY
      : ChunkLoadFailureReason.TRANSIENT;
  }
  return ChunkLoadFailureReason.UNKNOWN;
};

/**
 * Works out why a chunk failed to load by re-requesting the failed URL, since
 * the original `import()` error does not say whether the asset is missing
 * (stale deploy) or merely unreachable (network or CDN trouble).
 *
 * Never rejects: any unexpected failure resolves to `UNKNOWN`.
 */
export const classifyChunkLoadFailure = async (
  error: unknown,
): Promise<ChunkLoadFailureReason> => {
  if (!navigator.onLine) {
    return ChunkLoadFailureReason.NETWORK;
  }

  const url = getChunkLoadErrorUrl(error);
  if (!url) {
    return ChunkLoadFailureReason.UNKNOWN;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      signal: controller.signal,
    });
    // Only the status and headers matter; do not download the chunk body.
    response.body?.cancel().catch(() => {});
    return classifyStatus(response);
  } catch {
    return ChunkLoadFailureReason.NETWORK;
  } finally {
    clearTimeout(timeout);
  }
};
